/**
 * The counter's zone: the merchant's place, never the device's.
 *
 * <p>A moment and its local rendering are different things: an instant is UTC everywhere inside the
 * platform, and the clock a person reads it on belongs to the shop. `/pos/context` carries that zone
 * (`location.timezone`: the register's zone, else the business's regional setting, else a fallback), so
 * a tablet set to the wrong zone, or a phone carried across a border, must not change what the till
 * says the time is.
 *
 * <p>This is the one file that turns an instant into text. Every screen calls {@link formatInstant}
 * (or {@link formatCalendarDay} for a plain `YYYY-MM-DD`), and `scripts/check-counter-timezone.mjs`
 * fails the build on a date formatter that names no zone anywhere else.
 *
 * <p>The zone is the answer of the one shared `/pos/context` read (`posContext()`), stored here when it
 * arrives. Until then, and on a counter with no context at all (the sign-in page, an outage), the
 * device's own zone stands in: nothing else is known.
 */

import { useSyncExternalStore } from "react";

let contextZone: string | undefined;
const listeners = new Set<() => void>();

/** The zone this device is set to: the last resort, never the first choice. */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function isZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The context's zone when it names a real one; the device's zone otherwise. */
export function resolveCounterZone(fromContext?: string | null): string {
  const named = fromContext?.trim();
  return named && isZone(named) ? named : deviceZone();
}

/** Called with `location.timezone` when `/pos/context` answers, and with nothing when it is forgotten. */
export function setCounterZone(fromContext?: string | null): void {
  const next = fromContext?.trim() && isZone(fromContext.trim()) ? fromContext.trim() : undefined;
  if (next === contextZone) return;
  contextZone = next;
  listeners.forEach((listener) => listener());
}

/** The counter's zone right now, for code that is not a component. */
export function counterZone(): string {
  return contextZone ?? deviceZone();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The counter's zone, re-read when the context arrives. The server renders the device's (its own) zone. */
export function useCounterZone(): string {
  return useSyncExternalStore(subscribe, counterZone, deviceZone);
}

type Formattable = Date | string | number;

function toDate(value: Formattable): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * An instant as text on the counter's clock.
 *
 * `options` are the usual `Intl.DateTimeFormat` ones; `timeZone` defaults to the counter's and is
 * only passed when a caller must name another (tests). An unparseable value comes back as a dash
 * rather than throwing, because a bad timestamp must never take a screen down.
 */
export function formatInstant(
  value: Formattable,
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
  timeZone: string = counterZone(),
): string {
  const at = toDate(value);
  if (Number.isNaN(at.getTime())) return "\u2014";
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(at);
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A calendar day (`2026-09-30`, from the server's business-day fields) as text, with no zone shift.
 *
 * A plain date is not a moment. Read as midnight in some zone and formatted in another it moves a
 * day, so it is pinned to UTC on both sides: the day written is the day shown, on any device.
 * Anything that is not a plain date is returned untouched.
 */
export function formatCalendarDay(day: string, locale: string | undefined, options: Intl.DateTimeFormatOptions): string {
  const match = CALENDAR_DAY.exec(day ?? "");
  if (!match) return day;
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(at.getTime())) return day;
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(at);
}

/** The calendar day (`YYYY-MM-DD`) an instant falls on, on the counter's clock. */
export function zonedDay(value: Formattable, timeZone: string = counterZone()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).formatToParts(
    toDate(value),
  );
  const read = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${read("year")}-${read("month")}-${read("day")}`;
}
